import React, { useEffect, useState } from "react";
import {
  Button,
  Box,
  Stepper,
  Step,
  StepLabel,
  TextField,
  FormControl,
  FormGroup,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  Link,
} from "@mui/material";
import { FormikHelpers, useFormik } from "formik";
import * as Yup from "yup";
import { getSchools } from "../../services/schoolService";
import { School } from "../../types/School";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { StudentRegisterFormModel } from "../../types/StudentModel";
import { registerStudent } from "../../services/studentService";
import { enqueueSnackbar } from "notistack";
import SignInLayout from "../../layouts/SignInLayout";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

// Steps of the form
const steps = [
  "Contact Information",
  "Educational Background",
  "Account Setup",
];

// Component definition
const StudentSignUp: React.FC = () => {
  const [schools, setSchools] = useState<School[]>([]);
  const [step, setStep] = useState(0);
  const auth = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSchools = async () => {
      try {
        const response = await getSchools();
        setSchools(response.data);
      } catch (error) {
        console.error("Error fetching schools: ", error);
      }
    };
    fetchSchools();
  }, []);

  // Step-specific validation schema
  const validationSchema = [
    Yup.object({
      firstName: Yup.string().required("Required").min(2).max(255),
      lastName: Yup.string().required("Required").min(2).max(255),
      mobile: Yup.string().nullable().optional(),
      email: Yup.string().email("Invalid email format").required("Required"),
      dateOfBirth: Yup.date().required("Required"),
      city: Yup.string().required("Required"),
      country: Yup.string().required("Required"),
      currentLocation: Yup.string().required("Required"),
    }),
    Yup.object({
      currentSchool: Yup.string().required("Required"),
      currentSchoolYear: Yup.string().required("Required"),
      considerEAP: Yup.boolean(),
      institution: Yup.string().when("considerEAP", {
        is: true, // If considerEAP is true
        then: (schema) => schema.required("Institution is required"),
        otherwise: (schema) => schema.notRequired(), // Not required otherwise
      }),
      programInterest: Yup.string().when("considerEAP", {
        is: true, // If considerEAP is true
        then: (schema) => schema.required("Programme Interest is required"),
        otherwise: (schema) => schema.notRequired(), // Not required otherwise
      }),
    }),
    Yup.object({
      password: Yup.string()
        .min(8, "Password must be at least 8 characters long")
        .matches(/[a-z]/, "Password must contain at least one lowercase letter")
        .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
        .matches(/\d/, "Password must contain at least one number")
        .matches(
          /[^\w\s]/,
          "Password must contain at least one special character"
        )
        .required("Password is required"),
      confirmPassword: Yup.string()
        .required("Required")
        .oneOf([Yup.ref("password")], "Passwords must match"),
    }),
  ];

  // Formik configuration
  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      mobile: "",
      email: "",
      city: "",
      country: "",
      promotionCode: "",
      currentSchool: "",
      currentLocation: "",
      currentSchoolYear: "",
      dateOfBirth: "",
      considerEAP: false,
      institution: "",
      programInterest: "diploma",
      password: "",
      confirmPassword: "",
    },
    validationSchema: validationSchema[step],
    onSubmit: async (values, formikHelpers) => {
      console.log("Form Submitted:", values, formikHelpers);
      await handleSubmit(values, formikHelpers); // Call handleSubmit directly
    },
  });

  const handleSubmit = async (
    values: any,
    { setSubmitting }: FormikHelpers<any>
  ) => {
    try {
      const { confirmPassword, ...formValues } = values;
      const response = await registerStudent(formValues);
      if (response) {
        auth.setUser(response);
        navigate("/auth/email-confirmation", {
          state: { email: formValues.email },
        });
      }

      enqueueSnackbar(
        "Congrauations, You are successfully registered, please get your confirmation code from you email.",
        {
          variant: "success",
        }
      );

      console.log(response);
      // //registered but email not verified
      // if (data?.isActive == false) {
      //   props.navigateToEmailConfirmation(formik.values.email);
      // }
    } catch (error: any) {
      console.error(error);
      const errorMessage = error.response?.data?.error || error.message;

      enqueueSnackbar(`Error registering user: ${errorMessage}`, {
        variant: "error",
      });
      // handleError(error);
    }
    setSubmitting(false);
  };

  // Validate the current step before advancing
  const validateStep = async () => {
    const currentSchema = validationSchema[step];
    try {
      await currentSchema.validate(formik.values, { abortEarly: false });
      return true;
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        formik.setErrors(
          error.inner.reduce((acc: Record<string, string>, err) => {
            if (err.path) acc[err.path] = err.message;
            return acc;
          }, {})
        );
        // Mark all fields in the current step as touched
        error.inner.forEach((err) => {
          if (err.path) formik.setFieldTouched(err.path, true);
        });
      }
      return false;
    }
  };

  // Handle advancing to the next step
  const handleNext = async () => {
    const isStepValid = await validateStep();
    if (isStepValid) {
      if (step === steps.length - 1) {
        // No need to call formik.handleSubmit here, it will be triggered automatically
        // by the submit button with type="submit"
      } else {
        setStep(step + 1);
      }
    }
  };

  // Handle going back to the previous step
  const handleBack = () => {
    setStep(step - 1);
  };

  // Render the form fields for each step
  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <FormGroup>
            <FormControl>
              <TextField
                label="First Name"
                variant="outlined"
                name="firstName"
                autoComplete="off"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                error={
                  formik.touched.firstName && Boolean(formik.errors.firstName)
                }
                helperText={formik.touched.firstName && formik.errors.firstName}
                sx={{ mb: 2 }}
              />
            </FormControl>
            <FormControl>
              <TextField
                label="Last Name"
                variant="outlined"
                name="lastName"
                autoComplete="off"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                error={
                  formik.touched.lastName && Boolean(formik.errors.lastName)
                }
                helperText={formik.touched.lastName && formik.errors.lastName}
                sx={{ mb: 2 }}
              />
            </FormControl>
            <FormControl>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <MobileDatePicker
                  label="Date of Birth"
                  value={formik.values.dateOfBirth}
                  onChange={(value) => {
                    formik.setFieldValue("dateOfBirth", value); // Update Formik value
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error:
                        formik.touched.dateOfBirth &&
                        Boolean(formik.errors.dateOfBirth),
                      helperText:
                        formik.touched.dateOfBirth && formik.errors.dateOfBirth,
                    },
                  }}
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      paddingRight: 0,
                    },
                  }}
                />
              </LocalizationProvider>
            </FormControl>
            <FormControl>
              <TextField
                label="Mobile"
                variant="outlined"
                name="mobile"
                autoComplete="off"
                value={formik.values.mobile}
                onChange={formik.handleChange}
                error={formik.touched.mobile && Boolean(formik.errors.mobile)}
                helperText={formik.touched.mobile && formik.errors.mobile}
                sx={{ mb: 2 }}
              />
            </FormControl>
            <FormControl>
              <TextField
                label="Email"
                variant="outlined"
                type="email"
                name="email"
                autoComplete="off"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                sx={{ mb: 2 }}
              />
            </FormControl>
            <FormControl>
              <TextField
                label="City"
                variant="outlined"
                name="city"
                value={formik.values.city}
                onChange={formik.handleChange}
                error={formik.touched.city && Boolean(formik.errors.city)}
                helperText={formik.touched.city && formik.errors.city}
                sx={{ mb: 2 }}
              />
            </FormControl>
            <FormControl>
              <TextField
                label="Country"
                variant="outlined"
                name="country"
                value={formik.values.country}
                onChange={formik.handleChange}
                error={formik.touched.country && Boolean(formik.errors.country)}
                helperText={formik.touched.country && formik.errors.country}
                sx={{ mb: 2 }}
              />
            </FormControl>
            <FormControl>
              <TextField
                label="Current Location (country)"
                variant="outlined"
                name="currentLocation"
                value={formik.values.currentLocation}
                onChange={formik.handleChange}
                error={
                  formik.touched.currentLocation &&
                  Boolean(formik.errors.currentLocation)
                }
                helperText={
                  formik.touched.currentLocation &&
                  formik.errors.currentLocation
                }
                sx={{ mb: 2 }}
              />
            </FormControl>
          </FormGroup>
        );
      case 1:
        return (
          <FormGroup>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Current School or Agent</InputLabel>
              <Select
                name="currentSchool"
                value={formik.values.currentSchool}
                label="Current School or Agent"
                onChange={formik.handleChange}
                error={
                  formik.touched.currentSchool &&
                  Boolean(formik.errors.currentSchool)
                }
              >
                {schools.map((school: School) => (
                  <MenuItem key={school._id} value={school.name}>
                    {school.name}
                  </MenuItem>
                ))}
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Your Current School Year</InputLabel>
              <Select
                name="currentSchoolYear"
                value={formik.values.currentSchoolYear}
                label="Your Current School Year"
                onChange={formik.handleChange}
                error={
                  formik.touched.currentSchoolYear &&
                  Boolean(formik.errors.currentSchoolYear)
                }
              >
                <MenuItem value="belowYear10">Below Year 10</MenuItem>
                <MenuItem value="year10To12">Year 10-12</MenuItem>
                <MenuItem value="universityOrAbove">
                  University or Above
                </MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={formik.values.considerEAP}
                  onChange={formik.handleChange}
                  name="considerEAP"
                />
              }
              label="Will you consider taking EAP Space as a pathway for further studies?"
              sx={{
                mb: 2,
                color: (theme) => theme.palette.text.primary, // Use text.primary from the theme
              }}
            />
            {formik.values.considerEAP && (
              <>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <TextField
                    label="Which institution are you going to?"
                    name="institution"
                    value={formik.values.institution}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.institution &&
                      Boolean(formik.errors.institution)
                    }
                    helperText={
                      formik.touched.institution && formik.errors.institution
                    }
                  />
                </FormControl>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Which program are you mostly after?</InputLabel>
                  <Select
                    name="programInterest"
                    value={formik.values.programInterest}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.programInterest &&
                      Boolean(formik.errors.programInterest)
                    }
                  >
                    <MenuItem value="diploma">Diploma</MenuItem>
                    <MenuItem value="undergraduate">Undergraduate</MenuItem>
                    <MenuItem value="postgraduate">Postgraduate</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}
          </FormGroup>
        );
      case 2:
        return (
          <FormGroup>
            <FormControl>
              <TextField
                label="Password"
                variant="outlined"
                type="password"
                name="password"
                value={formik.values.password}
                onChange={formik.handleChange}
                error={
                  formik.touched.password && Boolean(formik.errors.password)
                }
                helperText={formik.touched.password && formik.errors.password}
                sx={{ mb: 2 }}
              />
            </FormControl>
            <FormControl>
              <TextField
                label="Confirm Password"
                variant="outlined"
                type="password"
                name="confirmPassword"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                error={
                  formik.touched.confirmPassword &&
                  Boolean(formik.errors.confirmPassword)
                }
                helperText={
                  formik.touched.confirmPassword &&
                  formik.errors.confirmPassword
                }
                sx={{ mb: 2 }}
              />
            </FormControl>
          </FormGroup>
        );
      default:
        return null;
    }
  };

  return (
    <SignInLayout>
      <Box sx={{ mt: 8 }}>
        <Grid item xs={12}>
          <Stepper
            sx={{
              ".MuiStepIcon-root": {
                "&:not(.Mui-active) .MuiStepIcon-text": {
                  fill: "white", // Apply white color for text in non-active steps
                },
              },
            }}
            activeStep={step}
            alternativeLabel
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Grid>
        <Grid item xs={12}>
          <form onSubmit={formik.handleSubmit}>
            <Box sx={{ mt: 4 }}>{renderStepContent(step)}</Box>
            <Box
              sx={{ mt: 2, display: "flex", justifyContent: "space-between" }}
            >
              <Button disabled={step === 0} onClick={handleBack}>
                Back
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={step === steps.length - 1 ? undefined : handleNext}
                type={step === steps.length - 1 ? "submit" : "button"}
              >
                {step === steps.length - 1 ? "Register" : "Next"}
              </Button>
            </Box>
          </form>
        </Grid>

        <Grid container sx={{ mt: 4 }}>
          <Grid item>
            <Link
              href="#"
              variant="body2"
              onClick={() => navigate("/auth/sign-in")}
            >
              {"Back to Sign-in"}
            </Link>
          </Grid>
        </Grid>
      </Box>
    </SignInLayout>
  );
};

export default StudentSignUp;
